import {
  rasterizeRotatedImage,
  rotatedImageIntersectsCrop,
  shouldRasterizeForCrop,
} from '../utils/rasterizeRotatedCrop';

const frameElementToCrop = (element, cropRect) => {
  const { x: cropX, y: cropY, width: cropWidth, height: cropHeight } = cropRect;
  const rotation = ((element.rotation % 360) + 360) % 360;
  const shifted = { ...element, x: element.x - cropX, y: element.y - cropY };

  if (element.type !== 'image' || rotation !== 0) {
    return shifted;
  }

  const left = element.x - element.width / 2;
  const top = element.y - element.height / 2;
  const right = left + element.width;
  const bottom = top + element.height;
  const intersectionX = Math.max(cropX, left);
  const intersectionY = Math.max(cropY, top);
  const intersectionRight = Math.min(cropX + cropWidth, right);
  const intersectionBottom = Math.min(cropY + cropHeight, bottom);
  const nextWidth = intersectionRight - intersectionX;
  const nextHeight = intersectionBottom - intersectionY;

  if (nextWidth < 1 || nextHeight < 1) {
    return { ...shifted, visible: false };
  }

  const { naturalWidth, naturalHeight } = element;
  const source = element.crop || { x: 0, y: 0, width: naturalWidth, height: naturalHeight };
  let relativeX = ((intersectionX - left) / element.width) * source.width;
  let relativeY = ((intersectionY - top) / element.height) * source.height;
  const relativeWidth = (nextWidth / element.width) * source.width;
  const relativeHeight = (nextHeight / element.height) * source.height;

  if (element.scaleX < 0) {
    relativeX = source.width - relativeX - relativeWidth;
  }
  if (element.scaleY < 0) {
    relativeY = source.height - relativeY - relativeHeight;
  }

  return {
    ...element,
    crop: {
      x: source.x + relativeX,
      y: source.y + relativeY,
      width: Math.max(1, relativeWidth),
      height: Math.max(1, relativeHeight),
    },
    width: nextWidth,
    height: nextHeight,
    x: intersectionX - cropX + nextWidth / 2,
    y: intersectionY - cropY + nextHeight / 2,
  };
};

export const buildCroppedElements = async (elements, cropRect) => {
  const nextElements = [];
  const created = [];

  for (const element of elements) {
    if (shouldRasterizeForCrop(element)) {
      if (!rotatedImageIntersectsCrop(element, cropRect)) {
        nextElements.push({
          ...element,
          x: element.x - cropRect.x,
          y: element.y - cropRect.y,
          visible: false,
        });
        continue;
      }

      const rasterized = await rasterizeRotatedImage(element, cropRect);
      if (!rasterized) {
        nextElements.push({
          ...element,
          x: element.x - cropRect.x,
          y: element.y - cropRect.y,
          visible: false,
        });
        continue;
      }
      created.push(rasterized);
      nextElements.push(rasterized);
      continue;
    }

    nextElements.push(frameElementToCrop(element, cropRect));
  }

  return { nextElements, created };
};
